import React, { useContext, useEffect, useState } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import InviteIcon from "@mui/icons-material/Mail";
import NotificationIcon from "@mui/icons-material/Notifications";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";
import Sidebar from "../sidebar/Sidebar";
import { styled } from "@mui/material/styles";
import { Outlet } from "react-router-dom";
import { USER_DATA } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { UserTypes } from "../../interfaces/UserTypes";
import { colors } from "../../assets/colors";
import { Menu, MenuItem, Badge as BadgeMui } from "@mui/material";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import {
  acceptInvite,
  addUserToProject,
  getInvitesRecived,
  rejectInvite,
} from "../../api/invites";
import Row from "./Row";
import ButtonLoading from "../ButtonLoading";
import { toast } from "react-toastify";
import { ProjectsContext } from "../../contexts/ProjectsContext";
import Badge from "../Badge";
import { createHistory } from "../../api/history";
import { mountHistory } from "../../utils/history";
import { ActionTypeEnum } from "../../interfaces/HistoryTypes";

export default function ContainerMain() {
  const drawerWidth = 240;
  const [open, setOpen] = useState(false);
  const [invitesRecived, setInvitesRecived] = useState([]);

  const [user, setUser] = useState<UserTypes | null>(null);
  const [pendingInvites, setPendingInvites] = useState<number>(0);
  const navigate = useNavigate();

  const { getUserProjects } = useContext(ProjectsContext);

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem(USER_DATA) || "{}"));
    fetchInvitesRecived();
  }, []);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const logout = () => {
    localStorage.clear();
    navigate("/login");
  };

  interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
  }

  const AppBar = styled(MuiAppBar)<AppBarProps>(({ theme, open }) => ({
    position: "fixed", // Garante que a Toolbar fique fixa no topo
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }));

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme, open }) => ({
    "& .MuiDrawer-paper": {
      position: "fixed", // Mantém o Drawer fixo na tela
      height: "100vh", // Garante que o Drawer ocupe toda a altura
      whiteSpace: "nowrap",
      width: drawerWidth,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: "border-box",
      ...(!open && {
        overflowX: "hidden",
        overflowY: "hidden",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up("sm")]: {
          width: theme.spacing(9),
        },
      }),
    },
  }));

  const fetchInvitesRecived = async () => {
    getInvitesRecived(
      JSON.parse(localStorage.getItem(USER_DATA) || "{}").uid
    ).then(async (response: any) => {
      // order response by created_at
      await response.sort((a: any, b: any) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      setInvitesRecived(response);

      const pendingInvites = response.filter(
        (invite: any) => invite.status === "pending"
      );
      setPendingInvites(pendingInvites.length);
    });
  };

  const acceptOrRejectInvite = (accepted: boolean, invite: any) => {
    if (accepted) {
      acceptInvite(invite.id)
        .then(() => {
          addUserToProject(invite)
            .then(() => {
              toast.success("Convite aceito com sucesso.");
              createHistory(
                mountHistory(
                  invite.project_uuid,
                  ActionTypeEnum.CREATE,
                  "Convite aceito para projeto: " + invite.project_name
                )
              );
              fetchInvitesRecived();
              getUserProjects();
            })
            .catch((e) => {
              console.log(e);
              toast.error("Ocorreu um problema, tente novamente mais tarde.");
            });
        })
        .catch((e) => {
          console.log(e);
          toast.error("Ocorreu um problema, tente novamente mais tarde.");
        });
    } else {
      rejectInvite(invite.id)
        .then(() => {
          toast.success("Convite recusado com sucesso.");
          createHistory(
            mountHistory(
              invite.project_uuid,
              ActionTypeEnum.CREATE,
              "Convite recusado para projeto: " + invite.project_name
            )
          );
          fetchInvitesRecived();
        })
        .catch((e) => {
          console.log(e);
          toast.error("Ocorreu um problema, tente novamente mais tarde.");
        });
    }
  };

  const renderResponseButton = (status: string, invite: any) => {
    switch (status) {
      case "pending":
        return (
          <Row
            style={{
              alignItems: "center",
              gap: 12,
              justifyContent: "flex-end",
              marginTop: 12,
            }}
          >
            <ButtonLoading
              label={"Aceitar"}
              onClick={() => {
                acceptOrRejectInvite(true, invite);
              }}
              style={{
                marginTop: 0,
                width: 100,
                height: 35,
                backgroundColor: colors.status.green,
                color: colors.white,
              }}
            />
            <ButtonLoading
              label={"Recusar"}
              isDelete
              onClick={() => {
                acceptOrRejectInvite(false, invite);
              }}
              style={{ marginTop: 0, width: 100, height: 35 }}
            />
          </Row>
        );
      case "accepted":
        return <Badge color={colors.status.green} text={"Aceito"} />;
      case "rejected":
        return <Badge color={colors.status.red} text={"Rejeitado"} />;

      default:
        return "Pendente";
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar open={open}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: "36px",
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <div
            style={{
              width: "100%",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <img
              src={process.env.PUBLIC_URL + "/logo-white.png"}
              height={40}
              onClick={() => navigate("/projects")}
            />
            <Typography
              component="span"
              variant="subtitle2"
              sx={{
                flexGrow: 1,
                marginLeft: 2,
                fontSize: 16,
                fontWeight: "bold",
                color: "#fff",
              }}
              className="header-title"
            >
              Ferramenta Qazando
            </Typography>
          </div>

          <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => (
              <React.Fragment>
                <IconButton size="medium" {...bindTrigger(popupState)}>
                  <NotificationIcon style={{ color: colors.white }} />
                </IconButton>
                <Menu {...bindMenu(popupState)}>
                  <MenuItem onClick={popupState.close}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Typography style={{ fontSize: 14, color: colors.black }}>
                        Nenhuma notificação pendente
                      </Typography>
                    </div>
                    <Divider />
                  </MenuItem>
                </Menu>
              </React.Fragment>
            )}
          </PopupState>

          <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => (
              <React.Fragment>
                <IconButton size="medium" {...bindTrigger(popupState)}>
                  <InviteIcon style={{ color: colors.white }} />
                  {pendingInvites > 0 && (
                    <BadgeMui badgeContent={pendingInvites} color="error" />
                  )}
                </IconButton>
                <Menu {...bindMenu(popupState)}>
                  {invitesRecived.length == 0 && (
                    <MenuItem onClick={popupState.close}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          style={{ fontSize: 14, color: colors.black }}
                        >
                          Nenhum convite pendente
                        </Typography>
                      </div>
                      <Divider />
                    </MenuItem>
                  )}
                  {invitesRecived.map((invite: any) => (
                    <Box onClick={popupState.close} sx={{ padding: 2 }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "end",
                          height: "100%",
                        }}
                      >
                        <span
                          style={{
                            whiteSpace: "pre-line",
                            fontSize: 12,
                            color: colors.black,
                            height: "100%",
                            paddingBottom: 16,
                          }}
                        >
                          Você tem um convite para participar do projeto:{" "}
                          <strong>{invite.project_name}</strong>.
                        </span>
                        {renderResponseButton(invite.status, invite)}
                      </div>
                      <Divider style={{ marginTop: 12 }} />
                    </Box>
                  ))}
                </Menu>
              </React.Fragment>
            )}
          </PopupState>

          <Box
            component="p"
            mr={1}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginLeft: 6,
            }}
          >
            <Typography
              component="span"
              variant="subtitle2"
              sx={{ flexGrow: 1, fontSize: 16, color: "#fff" }}
              className="header-username"
            >
              {user ? user.name : "User"}
            </Typography>

            <IconButton
              color="inherit"
              onClick={logout}
              size={"small"}
              style={{ marginLeft: 2 }}
            >
              <LogoutOutlined />
            </IconButton>
          </Box>
        </Toolbar>
        {/* <Row
          style={{
            justifyContent: "flex-start",
            backgroundColor: "#7A7A7AFF",
            padding: 8,
            gap: 6,
          }}
        >
          <i className="material-icons">diamond</i>
          <span>
            Seja Premium e tenha acesso completo a todas as funcionalidades. Clique aqui e assine agora!
          </span>
        </Row> */}
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List component="nav">
          <Sidebar />
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, paddingLeft: 8 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
